import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["cardsView", "tableView", "tableContainer"]
  static values = { initialView: String, tableId: Number }

  async connect() {
    // Initialize flags for table loading
    // Check if table content already exists (e.g., from Turbo cache)
    this.tableLoaded = this.hasTableContainerTarget && 
                       this.tableContainerTarget.querySelector('.productTable') !== null
    this.tableLoading = false
    
    // Get the saved preference from body data attribute (sitewide preference)
    const savedView = this.getSavedViewPreference()
    
    // Force update view state on every connect (handles Turbo cache restoration)
    await this.applyViewPreference(savedView)
    
    // Also listen for Turbo cache restoration events
    this.handleTurboRestore = this.handleTurboRestore.bind(this)
    document.addEventListener('turbo:load', this.handleTurboRestore)
  }
  
  getSavedViewPreference() {
    // Get preference from localStorage, fallback to default
    const localStoragePreference = localStorage.getItem('pulsar_view_preference')
    return localStoragePreference || this.initialViewValue || 'cards'
  }

  disconnect() {
    // Remove event listeners
    document.removeEventListener('turbo:load', this.handleTurboRestore)
    
    // Clean up any pending timeouts
    if (this.tableLoadTimeout) {
      clearTimeout(this.tableLoadTimeout)
    }
    
    // Clean up fixed headers and clear from registry
    document.querySelectorAll('[id^="fixedtableheader"]').forEach(header => {
      const fixedID = '#' + header.id;
      $(window).off('scroll.' + header.id);
      $(window).off('resize.' + header.id);
      header.remove();
    });
    
    
    // Note: Turbo Frames automatically handle cleanup of their content,
    // so we don't need to manually destroy DataTables or clear containers
    
    // Reset flags
    this.tableLoaded = false
    this.tableLoading = false
  }

  async applyViewPreference(view) {
    // Apply view preference to ALL tables on the page
    this.updateAllButtonStates(view)
    await this.switchAllViews(view)
  }

  handleTurboRestore() {
    // Re-apply view preference after Turbo renders cached page
    const savedView = this.getSavedViewPreference()
    // Use requestAnimationFrame to ensure DOM is ready after Turbo restore
    requestAnimationFrame(async () => {
      await this.applyViewPreference(savedView)
    })
  }



  async switchView(event) {
    const targetView = event.target.dataset.view
    
    // Prevent double clicks during transition
    if (this.switching) return
    this.switching = true
    
    // Save preference to localStorage
    this.saveViewPreference(targetView)
    
    // Update button states for ALL tables on the page
    this.updateAllButtonStates(targetView)
    
    // Show/hide views for ALL tables on the page
    await this.switchAllViews(targetView)
    
    // Reset switching flag
    this.switching = false
  }

  saveViewPreference(view) {
    // Save preference to localStorage
    localStorage.setItem('pulsar_view_preference', view)
    
    // Update body data attribute for consistency
    document.body.dataset.productViewPreference = view
    
    // Note: Removed Turbo cache clearing to prevent visual flashing
    // View preferences are maintained via localStorage and body data attributes
  }


  updateButtonStates(activeView) {
    this.element.querySelectorAll('.view-toggle-btn').forEach(btn => {
      btn.classList.remove('active')
      if (btn.dataset.view === activeView) {
        btn.classList.add('active')
      }
    })
  }

  updateAllButtonStates(activeView) {
    // Update button states for ALL view-toggle controllers on the page
    document.querySelectorAll('[data-controller*="view-toggle"] .view-toggle-btn').forEach(btn => {
      btn.classList.remove('active')
      if (btn.dataset.view === activeView) {
        btn.classList.add('active')
      }
    })
  }

  async switchAllViews(targetView) {
    // Simple, clean view switching - let existing scroll preservation handle positioning
    document.querySelectorAll('[data-controller*="view-toggle"]').forEach(wrapper => {
      const cardsView = wrapper.querySelector('[data-view-toggle-target="cardsView"]')
      const tableView = wrapper.querySelector('[data-view-toggle-target="tableView"]')
      
      if (targetView === 'cards') {
        if (tableView) tableView.classList.add('hidden')
        if (cardsView) cardsView.classList.remove('hidden')
      } else {
        if (cardsView) cardsView.classList.add('hidden')
        if (tableView) tableView.classList.remove('hidden')
        
        // Load table content if needed (non-blocking)
        const controllerApplication = this.application
        const controllerInstance = controllerApplication.getControllerForElementAndIdentifier(wrapper, 'view-toggle')
        
        if (controllerInstance && 
            typeof controllerInstance.loadTableContent === 'function' && 
            !controllerInstance.tableLoaded && 
            !controllerInstance.tableLoading) {
          controllerInstance.loadTableContent()
        }
      }
      
      // Dispatch event to notify table-filter controllers that view has switched
      wrapper.dispatchEvent(new CustomEvent('view-toggle:switched', {
        detail: { targetView },
        bubbles: true
      }))
    })
    
    // Only reapply filters if switching to cards view or if table is already loaded
    // This prevents filter reapplication during table loading (which has its own reapplyFilters call)
    if (targetView === 'cards') {
      this.reapplyFilters()
    } else {
      // For table view, only reapply if table is already loaded
      // If not loaded, loadTableContent() will handle filter reapplication
      const hasLoadedTable = document.querySelector('[data-controller*="view-toggle"] .productTable')
      if (hasLoadedTable) {
        this.reapplyFilters()
      }
    }
  }

  showCards() {
    if (this.hasCardsViewTarget) {
      this.cardsViewTarget.classList.remove('hidden')
    }
    if (this.hasTableViewTarget) {
      this.tableViewTarget.classList.add('hidden')
      
      // Clean up fixed headers when hiding table view
      document.querySelectorAll('[id^="fixedtableheader"]').forEach(header => {
        const fixedID = '#' + header.id;
        $(window).off('scroll.' + header.id);
        $(window).off('resize.' + header.id);
        header.remove();
      });
      
    }
  }

  async showTable() {
    if (this.hasCardsViewTarget) {
      this.cardsViewTarget.classList.add('hidden')
    }
    if (this.hasTableViewTarget) {
      this.tableViewTarget.classList.remove('hidden')
      
      // Load table content if not already loaded (non-blocking)
      if (!this.tableLoaded && !this.tableLoading) {
        this.loadTableContent() // Don't await - this will handle header initialization
      } else if (this.tableLoaded) {
        // Table is already loaded, initialize header on next frame
        requestAnimationFrame(() => this.initializeFixedTableHeader())
      }
    }
  }

  initializeFixedTableHeader() {
    // Just call the global function which handles duplicate prevention
    if (window.setFixedTableHeader) {
      window.setFixedTableHeader();
    }
  }
  

  async loadTableContent() {
    if (this.tableLoading || this.tableLoaded || !this.hasTableContainerTarget) {
      return
    }

    this.tableLoading = true
    
    // Hide container completely during loading
    this.tableContainerTarget.classList.add('loading')
    // Use opacity to hide while preserving layout for width calculations
    this.tableContainerTarget.style.opacity = '0'
    this.tableContainerTarget.style.pointerEvents = 'none'
    
    // Also hide footer elements that contribute to flickering
    this.hideTableFooterElements()
    
    try {
      // If this is a turbo-frame, use Turbo's native frame loading
      if (this.tableContainerTarget.tagName === 'TURBO-FRAME') {
        // Set the src attribute to trigger Turbo Frame loading
        this.tableContainerTarget.src = `/load_table_content?table_id=${this.tableIdValue}`
        
        // Listen for turbo:frame-load event
        const frameLoadHandler = () => {
          this.tableLoaded = true
          this.handleTableLoaded()
          // Remove the event listener after handling
          this.tableContainerTarget.removeEventListener('turbo:frame-load', frameLoadHandler)
        }
        
        this.tableContainerTarget.addEventListener('turbo:frame-load', frameLoadHandler)
      } else {
        // Fallback to Turbo Streams
        const response = await fetch(`/load_table_content?table_id=${this.tableIdValue}`, {
          method: 'GET',
          headers: {
            'Accept': 'text/vnd.turbo-stream.html, text/html',
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
        
        if (response.ok) {
          const contentType = response.headers.get('content-type')
          
          if (contentType && contentType.includes('turbo-stream')) {
            // Let Turbo handle the stream response
            const html = await response.text()
            Turbo.renderStreamMessage(html)
            
            // Mark as loaded after Turbo processes the stream
            this.tableLoaded = true
            
            // Wait for Turbo to finish rendering
            requestAnimationFrame(() => {
              this.handleTableLoaded()
            })
          } else {
            // Fallback to HTML response
            const html = await response.text()
            this.tableContainerTarget.innerHTML = html
            this.tableLoaded = true
            this.handleTableLoaded()
          }
        } else {
          throw new Error('Failed to load table content')
        }
      }
    } catch (error) {
      console.error('Error loading table:', error)
      this.tableContainerTarget.innerHTML = '<div class="error-loading-table">Error loading table content</div>'
      this.tableContainerTarget.classList.remove('loading')
      this.tableContainerTarget.classList.add('ready')
      this.showTableFooterElements()
      this.tableLoading = false
    }
  }
  
  handleTableLoaded() {
    // Ensure container is hidden during operations
    this.tableContainerTarget.style.opacity = '0'
    this.tableContainerTarget.style.pointerEvents = 'none'
    
    // Find newly loaded tables
    const tables = this.tableContainerTarget.querySelectorAll('.productTable')
    
    // Hide tables individually for extra safety
    tables.forEach(table => {
      table.classList.add('initializing')
      table.style.opacity = '0'
    })
    
    // Force a layout to ensure dimensions are calculated
    this.tableContainerTarget.offsetHeight
    
    // Do all manipulations while hidden
    // Use setTimeout to ensure previous style changes are applied
    setTimeout(() => {
      // 1. Apply filters
      this.reapplyFilters()
      
      // 2. Initialize table resize (needs proper dimensions)
      this.initializeTableResize()
      
      // 3. Force another layout calculation after resize
      this.tableContainerTarget.offsetHeight
      
      // 4. Now show everything
      setTimeout(() => {
        // Update classes
        tables.forEach(table => {
          table.classList.remove('initializing')
          table.classList.add('ready')
          table.style.opacity = ''
        })
        
        this.tableContainerTarget.classList.remove('loading')
        this.tableContainerTarget.classList.add('ready')
        
        // Show container
        this.tableContainerTarget.style.opacity = ''
        this.tableContainerTarget.style.pointerEvents = ''
        
        // Show footer elements
        this.showTableFooterElements()
        
        // Initialize fixed headers after everything is stable
        setTimeout(() => {
          this.initializeFixedTableHeader()
        }, 500)
      }, 50) // Small delay for calculations to complete
    }, 0) // Ensure style changes are applied first
  }

  reapplyFilters() {
    // Find all table-filter controllers on the page and reapply their filters
    document.querySelectorAll('[data-controller*="table-filter"]').forEach(wrapper => {
      const controllerApplication = this.application
      const filterController = controllerApplication.getControllerForElementAndIdentifier(wrapper, 'table-filter')
      
      if (filterController && typeof filterController.applyFilters === 'function') {
        // Apply filters synchronously for immediate response
        filterController.clearAllFilterClasses()
        filterController.applyFilters()
      }
    })
  }

  hideTableFooterElements() {
    // Find all footer elements that contribute to flickering within this controller's scope
    const footerElements = this.element.querySelectorAll('.productFooter, strong.notes, dl.notes')
    footerElements.forEach(element => {
      element.classList.add('loading-hidden')
    })
    
    // Also hide filter areas to prevent connector filter computation flashing
    const filterAreas = this.element.querySelectorAll('.product-filters')
    filterAreas.forEach(element => {
      element.classList.add('loading-hidden')
    })
  }

  showTableFooterElements() {
    // Show all footer elements after table loading is complete
    // Batch DOM reads and writes for better performance
    requestAnimationFrame(() => {
      const footerElements = this.element.querySelectorAll('.productFooter, strong.notes, dl.notes')
      const filterAreas = this.element.querySelectorAll('.product-filters')
      
      // Remove loading-hidden class and let CSS transition handle the fade-in
      footerElements.forEach(element => {
        element.classList.remove('loading-hidden')
        // Reset inline styles to allow CSS transitions
        element.style.visibility = ''
        element.style.opacity = ''
      })
      filterAreas.forEach(element => {
        element.classList.remove('loading-hidden')
        element.style.visibility = ''
        element.style.opacity = ''
      })
    })
  }

  initializeTableResize() {
    const currentWidth = window.innerWidth
    console.log(`AJAX table: viewport ${currentWidth}px - initializing table resize`);
    
    // Initialize resize for new tables
    if (window.resizeTableInit && window.resizeTable) {
      const newTables = this.tableContainerTarget.querySelectorAll('.productTable')
      if (newTables.length > 0) {
        console.log(`Initializing resize for ${newTables.length} AJAX-loaded tables`);
        window.resizeTableInit(".productTable", 954);
        window.resizeTable();
      }
    }
  }
}